import { motion } from 'framer-motion'
import type { Adventure } from '../../types'
import { Calculator, Star, Clock } from 'lucide-react'

interface AdventureCardProps {
  adventure: Adventure
  isCompleted: boolean
  isLocked?: boolean
  onClick: () => void
}

// Theme colors for different math topics
const THEME_COLORS: Record<string, { bg: string; icon: string; accent: string }> = {
  counting: { bg: 'from-orange-100 to-orange-50', icon: '🔢', accent: '#FF6B35' },
  addition: { bg: 'from-teal-100 to-teal-50', icon: '➕', accent: '#4ECDC4' },
  patterns: { bg: 'from-purple-100 to-purple-50', icon: '🧩', accent: '#9B5DE5' },
  numbers: { bg: 'from-yellow-100 to-yellow-50', icon: '🔟', accent: '#FFE66D' },
  shapes: { bg: 'from-blue-100 to-blue-50', icon: '🔷', accent: '#4A90D9' },
}

export default function AdventureCard({ adventure, isCompleted, isLocked, onClick }: AdventureCardProps) {
  const theme = THEME_COLORS[adventure.theme] || THEME_COLORS.counting

  return (
    <motion.button
      whileHover={!isLocked ? { scale: 1.02, y: -4 } : {}}
      whileTap={!isLocked ? { scale: 0.98 } : {}}
      onClick={!isLocked ? onClick : undefined}
      disabled={isLocked}
      className={`
        relative w-full text-left overflow-hidden
        bg-gradient-to-br ${theme.bg}
        rounded-3xl p-5
        ${isLocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer shadow-lg hover:shadow-xl'}
        transition-shadow duration-300
      `}
    >
      {/* Completed Badge */}
      {isCompleted && (
        <motion.div 
          className="absolute top-3 right-3 w-8 h-8 bg-[var(--math-success)] rounded-full flex items-center justify-center shadow-md"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring" }}
        >
          <Star className="w-4 h-4 text-white fill-white" />
        </motion.div>
      )}

      {/* Locked Badge */}
      {isLocked && (
        <div className="absolute top-3 right-3 w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
          <span className="text-sm">🔒</span>
        </div>
      )}

      {/* Icon */}
      <div className="text-4xl mb-3">{theme.icon}</div>

      {/* Title */}
      <h3 className="text-lg font-bold text-gray-800 mb-1 leading-tight">
        {adventure.title}
      </h3>

      {/* Meta Info */}
      <div className="flex items-center gap-3 text-sm text-gray-500 mt-3">
        <div className="flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" />
          <span>{adventure.estimatedDuration} min</span>
        </div>
        <div className="flex items-center gap-1">
          <Calculator className="w-3.5 h-3.5" />
          <span className="capitalize">{adventure.theme}</span>
        </div>
      </div>

      {/* Progress Bar (if in progress) */}
      {!isCompleted && !isLocked && (
        <div className="mt-3">
          <div className="h-1.5 bg-white/50 rounded-full overflow-hidden">
            <motion.div 
              className="h-full rounded-full"
              style={{ backgroundColor: theme.accent }}
              initial={{ width: 0 }}
              animate={{ width: '20%' }}
              transition={{ duration: 0.5, delay: 0.2 }}
            />
          </div>
        </div>
      )}
    </motion.button>
  )
}
